'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { StarInput } from '@/components/reviews/StarRating';
import { REVIEW_CONTEXTS, isReviewContext, submitPublicReview } from '@/lib/reviews/reviewsService';

const MAX_REVIEW = 3000;
const MIN_REVIEW = 10;

export default function ReviewForm() {
  const params = useSearchParams();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [context, setContext] = useState<string>('webinar');
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const n = params?.get('name');
    const e = params?.get('email');
    const c = params?.get('context');
    if (n) setFullName(n);
    if (e) setEmail(e);
    if (c && isReviewContext(c)) setContext(c);
  }, [params]);

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (!fullName.trim()) return setError('Please enter your name.');
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError('Please enter a valid email address.');
    if (rating < 1) return setError('Please choose a star rating.');
    if (review.trim().length < MIN_REVIEW) return setError(`Please write at least ${MIN_REVIEW} characters in your review.`);

    if (website) {
      setDone(true);
      return;
    }

    setSubmitting(true);
    try {
      await submitPublicReview({
        fullName: fullName.trim(),
        email: email.trim(),
        context,
        rating,
        review: review.trim(),
        displayName: displayName.trim(),
        businessName: businessName.trim(),
        consentToPublish: consent,
        source: params?.get('source') || 'review-page',
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div role="status" className="rounded-2xl bg-white p-8 text-center shadow-sm">
        <CheckCircle2 size={44} className="mx-auto text-[#C6A667]" aria-hidden="true" />
        <p className="mt-4 text-xl font-extrabold text-[#581C32]">Thank you for your review. It means a lot.</p>
        <Link href="/" className="mt-6 inline-block text-sm font-semibold text-[#581C32] underline underline-offset-2">
          Back to Care Business School
        </Link>
      </div>
    );
  }

  const inputCls =
    'mt-1.5 w-full rounded-lg border border-[#581C32]/25 bg-white px-3.5 py-3 text-base text-[#2B0D18] placeholder:text-[#2B0D18]/40 focus:border-[#581C32] focus:outline-none focus:ring-2 focus:ring-[#C6A667]';
  const labelCls = 'block text-sm font-semibold text-[#581C32]';

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5 rounded-2xl bg-white p-5 shadow-sm sm:p-8">
      <div>
        <label htmlFor="rv-name" className={labelCls}>
          Your name <span aria-hidden="true">*</span>
        </label>
        <input id="rv-name" type="text" required autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} />
      </div>

      <div>
        <label htmlFor="rv-email" className={labelCls}>
          Email <span aria-hidden="true">*</span>
        </label>
        <input id="rv-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-describedby="rv-email-hint" className={inputCls} />
        <p id="rv-email-hint" className="mt-1 text-xs text-[#2B0D18]/60">Not shown publicly</p>
      </div>

      <div>
        <label htmlFor="rv-context" className={labelCls}>What are you reviewing?</label>
        <select id="rv-context" value={context} onChange={(e) => setContext(e.target.value)} className={inputCls}>
          {REVIEW_CONTEXTS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      <div>
        <span id="rv-rating-label" className={labelCls}>
          Your rating <span aria-hidden="true">*</span>
        </span>
        <div className="mt-1" aria-labelledby="rv-rating-label">
          <StarInput value={rating} onChange={setRating} describedBy="rv-rating-label" />
        </div>
      </div>

      <div>
        <label htmlFor="rv-review" className={labelCls}>
          Your review <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="rv-review"
          required
          rows={6}
          maxLength={MAX_REVIEW}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          aria-describedby="rv-review-count"
          className={inputCls}
        />
        <p id="rv-review-count" className="mt-1 text-right text-xs text-[#2B0D18]/60">
          {review.length} / {MAX_REVIEW}
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="rv-display" className={labelCls}>Name to show with your review</label>
          <input id="rv-display" type="text" placeholder="e.g. Sarah T." value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label htmlFor="rv-business" className={labelCls}>Your care business name</label>
          <input id="rv-business" type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="rv-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-5 w-5 flex-shrink-0 rounded border-[#581C32]/40 accent-[#581C32]"
        />
        <label htmlFor="rv-consent" className="text-sm text-[#2B0D18]">
          You can share my review on the Care Business School website.
        </label>
      </div>

      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="rv-website">Website</label>
        <input id="rv-website" type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {error}
        </p>
      )}

      <div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#581C32] px-6 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#6d2440] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C6A667] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
          {submitting ? 'Sending…' : 'Submit review'}
        </button>
        <p className="mt-3 text-center text-xs text-[#2B0D18]/70">
          We publish genuine reviews only, whether positive or negative. See our{' '}
          <Link href="/privacy-policy" className="font-semibold text-[#581C32] underline underline-offset-2">
            Privacy Notice
          </Link>
          .
        </p>
      </div>
    </form>
  );
}
